import { Capabilities, EnvFacts, GyroState } from './types'

export function detectCapabilities(facts: EnvFacts): Capabilities {
  const ua = facts.userAgent
  const isIPhone = /iPhone|iPod/.test(ua)
  const isIPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && facts.maxTouchPoints > 1)
  const isIOSFamily = isIPhone || isIPad
  // Desktop Safari: says Safari, and is none of the Chromium / Firefox /
  // Android browsers that also put "Safari" in their UA (Brave and Edge
  // report as Chrome). Mirrors PlaybackCore.shouldUseNativeHls, which asks
  // video.js the same question at playback time.
  const isDesktopSafari = /Safari\//.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|Android|FxiOS|Firefox/.test(ua)

  let gyro: GyroState = 'unavailable'
  if (facts.deviceOrientation) {
    gyro = facts.deviceOrientationPermission ? 'needs-permission' : 'available'
  }

  return {
    webgl2: facts.webgl,
    mse: facts.mse || facts.managedMSE,
    nativeHLS: facts.nativeHLS,
    forcesNativeHls: isIOSFamily || isDesktopSafari,
    isIPhone,
    isIPad,
    isIOSFamily,
    rVFC: facts.rVFC,
    gyro,
    webAudio: facts.audioContext,
    touch: facts.maxTouchPoints > 0,
  }
}

// Thin, browser-only adapter that reads real globals. Not unit-tested
// (no jsdom); covered by the manual device-QA matrix.
export function readEnvironment(): EnvFacts {
  const nav = navigator
  let webgl = false
  try {
    const canvas = document.createElement('canvas')
    webgl = !!(canvas.getContext('webgl2') || canvas.getContext('webgl'))
  } catch {
    webgl = false
  }
  let nativeHLS = false
  try {
    nativeHLS = !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl')
  } catch {
    nativeHLS = false
  }
  const w = window as unknown as {
    MediaSource?: unknown
    ManagedMediaSource?: unknown
    AudioContext?: unknown
    webkitAudioContext?: unknown
    DeviceOrientationEvent?: { requestPermission?: unknown }
  }
  return {
    userAgent: nav.userAgent,
    maxTouchPoints: nav.maxTouchPoints || 0,
    webgl,
    mse: typeof w.MediaSource !== 'undefined',
    managedMSE: typeof w.ManagedMediaSource !== 'undefined',
    nativeHLS,
    rVFC: 'requestVideoFrameCallback' in HTMLVideoElement.prototype,
    audioContext: typeof w.AudioContext !== 'undefined' || typeof w.webkitAudioContext !== 'undefined',
    deviceOrientation: typeof w.DeviceOrientationEvent !== 'undefined',
    deviceOrientationPermission:
      typeof w.DeviceOrientationEvent !== 'undefined' &&
      typeof w.DeviceOrientationEvent?.requestPermission === 'function',
  }
}
